/**
 * RunsSection — Life › Health › Runs.
 * Runs are logged by telling NEXA in chat ("I ran 5k in 27:12"). She parses
 * distance / duration / HR and writes to nexa_run_workouts. This section
 * simply reads that collection and shows week + 30-day totals plus the list.
 */
import { useEffect, useState } from 'react';
import { Activity, ChevronDown, MessageSquare, Timer, Flame, Heart, MapPin, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { nexaApi } from './api';

const COLLAPSE_KEY = 'nexa_runs_collapsed';

const runDur = (w) => w.duration_s || (
  w.end && w.start ? Math.max(0, (new Date(w.end).getTime() - new Date(w.start).getTime()) / 1000) : 0
);

const fmtDist = (m) => (m == null ? '—' : (m / 1000).toFixed(2));
const fmtDur = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const ss = n % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
};
const fmtPace = (m, s) => {
  if (!m || !s) return '—';
  const paceMin = (s / 60) / (m / 1000);
  const mm = Math.floor(paceMin);
  const ss = Math.round((paceMin - mm) * 60);
  return `${mm}:${String(ss).padStart(2, '0')}/km`;
};
const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB',
      { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
};

/* One run row. */
const RunRow = ({ run, onDelete }) => {
  const dur = run.duration_s || (
    run.end && run.start
      ? Math.max(0, (new Date(run.end).getTime() - new Date(run.start).getTime()) / 1000)
      : 0
  );
  return (
    <div className="px-5 py-4 flex items-start gap-4" data-testid={`nexa-run-${run.id}`}>
      <div className="shrink-0 w-11 h-11 rounded-xl bg-sky-400/10 border border-sky-400/25 flex items-center justify-center">
        <Activity className="w-5 h-5 text-sky-400" strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-white/85">{fmtDay(run.start)}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
          <span className="font-mono text-sky-300" data-testid={`nexa-run-${run.id}-distance`}>
            {fmtDist(run.distance_m)} km
          </span>
          <span className="flex items-center gap-1 font-mono text-white/70">
            <Timer className="w-3 h-3" /> {fmtDur(dur)}
          </span>
          <span className="font-mono text-white/60">
            {fmtPace(run.distance_m, dur)}
          </span>
          {run.avg_hr ? (
            <span className="flex items-center gap-1 font-mono text-white/60">
              <Heart className="w-3 h-3" /> {Math.round(run.avg_hr)}
            </span>
          ) : null}
          {run.kcal ? (
            <span className="flex items-center gap-1 font-mono text-white/60">
              <Flame className="w-3 h-3" /> {Math.round(run.kcal)}
            </span>
          ) : null}
          {run.elevation_gain_m ? (
            <span className="flex items-center gap-1 font-mono text-white/50">
              <MapPin className="w-3 h-3" /> {Math.round(run.elevation_gain_m)} m
            </span>
          ) : null}
        </div>
        {run.notes ? (
          <p className="mt-1 text-[11px] text-white/40">{run.notes}</p>
        ) : null}
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Delete this run from NEXA?')) {
              onDelete(run.id);
            }
          }}
          data-testid={`nexa-run-${run.id}-delete`}
          className="shrink-0 w-8 h-8 rounded-full text-white/25 hover:text-white/80 active:scale-90 transition-all text-xs"
          aria-label="Delete run"
        >
          ✕
        </button>
      )}
    </div>
  );
};

export const RunsSection = () => {
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const load = () => nexaApi.get('/health/workouts')
    .then(({ data: d }) => setData(d))
    .catch(() => setData({ workouts: [], totals: { week: {}, month: {} } }));
  useEffect(() => {
    load();
    nexaApi.get('/health/run-goal').then(({ data: d }) => setGoal(d.goal_km)).catch(() => setGoal(20));
  }, []);
  const editGoal = async () => {
    const v = window.prompt('Weekly distance goal (km):', goal ?? 20);
    if (v === null) return;
    const n = parseFloat(v);
    if (Number.isNaN(n) || n < 0) return;
    try {
      const { data: d } = await nexaApi.put('/health/run-goal', { goal_km: n });
      setGoal(d.goal_km);
      toast.success(`Weekly goal set to ${d.goal_km} km`);
    } catch { toast.error('Could not save goal'); }
  };
  const open = !collapsed;
  const toggle = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  const del = async (id) => {
    setData((prev) => prev ? ({ ...prev, workouts: prev.workouts.filter((w) => w.id !== id) }) : prev);
    try { await nexaApi.delete(`/health/workouts/${id}`); } catch { toast.error('Could not delete'); load(); }
  };

  const workouts = data?.workouts || [];
  const week = data?.totals?.week || {};
  const weekKm = ((week.distance_m || 0) / 1000).toFixed(1);

  return (
    <section data-testid="nexa-runs-section">
      <button
        type="button"
        onClick={toggle}
        data-testid="nexa-runs-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between mb-3 text-left"
      >
        <span className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          <motion.span
            animate={{ rotate: open ? 0 : -90 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className="inline-flex"
          >
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
          </motion.span>
          Runs
        </span>
        <span className="text-[11px] font-mono text-white/50">
          {week.runs || 0} this wk · {weekKm} km
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-4">
              {/* Chat hint — always visible */}
              <div className="rounded-2xl border border-sky-400/20 bg-sky-500/[0.04] px-4 py-3 flex items-start gap-3"
                   data-testid="nexa-runs-chat-hint">
                <MessageSquare className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" strokeWidth={1.7} />
                <p className="text-[12px] text-white/70 leading-relaxed">
                  Finished a run? Just tell NEXA in chat — e.g.{' '}
                  <span className="text-sky-300 font-mono">&ldquo;I ran 5.2k in 27:48&rdquo;</span>{' '}
                  or <span className="text-sky-300 font-mono">&ldquo;did 8km easy, HR 145&rdquo;</span>.
                  She&rsquo;ll log it here.
                </p>
              </div>

              {/* Week + month totals */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4" data-testid="nexa-runs-week">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">This week</div>
                  <div className="nexa-heading text-3xl font-light mt-1">
                    {weekKm}<span className="text-lg text-white/40 ml-1">km</span>
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5 font-mono">
                    {week.runs || 0} runs · {fmtDur(week.duration_s || 0)}
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4" data-testid="nexa-runs-month">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">Last 30 d</div>
                  <div className="nexa-heading text-3xl font-light mt-1">
                    {((data?.totals?.month?.distance_m || 0) / 1000).toFixed(1)}
                    <span className="text-lg text-white/40 ml-1">km</span>
                  </div>
                  <div className="text-[11px] text-white/50 mt-0.5 font-mono">
                    {data?.totals?.month?.runs || 0} runs · {fmtDur(data?.totals?.month?.duration_s || 0)}
                  </div>
                </div>
              </div>

              {/* Weekly goal + pace trend */}
              {(() => {
                const gk = goal ?? 20;
                const wk = Number(weekKm);
                const pct = gk > 0 ? Math.min(100, (wk / gk) * 100) : 0;
                const paceData = [...workouts].reverse()
                  .filter((w) => w.distance_m && runDur(w) > 0)
                  .map((w) => ({
                    date: (w.start || '').slice(5, 10),
                    pace: Number(((runDur(w) / 60) / (w.distance_m / 1000)).toFixed(2)),
                  }));
                return (
                  <>
                    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4" data-testid="nexa-runs-goal">
                      <div className="flex items-center justify-between mb-2.5">
                        <span className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white/40">
                          <Target className="w-3.5 h-3.5 text-sky-400" /> Weekly goal
                        </span>
                        <button onClick={editGoal} data-testid="nexa-runs-goal-edit"
                          className="text-[11px] text-sky-300 hover:text-sky-200 active:scale-95">Edit</button>
                      </div>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span className="nexa-heading text-2xl font-light">{wk}<span className="text-sm text-white/40"> / {gk} km</span></span>
                        <span className="text-[11px] font-mono text-white/50" data-testid="nexa-runs-goal-pct">{Math.round(pct)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                        <motion.div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-400' : 'bg-sky-400'}`}
                          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5, ease: 'easeOut' }} />
                      </div>
                      <p className="text-[11px] text-white/40 mt-2">
                        {pct >= 100 ? 'Goal smashed for the week — nice work.' : `${(gk - wk).toFixed(1)} km to go this week.`}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4" data-testid="nexa-runs-pace-trend">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">Pace trend · min/km (lower is faster)</span>
                      {paceData.length >= 2 ? (
                        <div className="h-40 mt-3 -ml-3">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={paceData}>
                              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} />
                              <YAxis domain={['dataMin - 0.3', 'dataMax + 0.3']} reversed
                                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} axisLine={false} tickLine={false} width={38}
                                tickFormatter={(v) => `${Math.floor(v)}:${String(Math.round((v - Math.floor(v)) * 60)).padStart(2, '0')}`} />
                              <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 }}
                                formatter={(v) => [`${Math.floor(v)}:${String(Math.round((v - Math.floor(v)) * 60)).padStart(2, '0')} /km`, 'Pace']} />
                              <Line type="monotone" dataKey="pace" stroke="#38BDF8" strokeWidth={2} dot={{ fill: '#38BDF8', r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="text-sm text-white/40 mt-3">Log at least two runs to see your pace trend.</p>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Runs list */}
              <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-runs-list">
                {data === null && (
                  <div className="px-5 py-6 text-sm text-white/40">Loading runs…</div>
                )}
                {data !== null && workouts.length === 0 && (
                  <div className="px-5 py-6 text-sm text-white/50">
                    No runs yet. Tell NEXA about your next one in chat.
                  </div>
                )}
                {workouts.map((w, idx) => (
                  <div key={w.id} className={idx < workouts.length - 1 ? 'border-b border-white/[0.06]' : ''}>
                    <RunRow run={w} onDelete={del} />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default RunsSection;
